import { BasicSearchFilter } from 'src/models/searchFilter';
import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import {
  ENSURE_ALL_AVAILABLE_COMMAND_TYPES_FOR_USER,
  INITIAL_AUTHENTICATION,
  LOAD_ALL_AVAILABLE_COMMAND_TYPES_FOR_USER,
} from '../../actions/actionTypes';
import { Action } from '../../actions/commonActions';
import {
  loadUserCommandTypes,
  loadUserCommandTypesFailure,
  loadUserCommandTypesPending,
  loadUserCommandTypesSuccess,
} from '../../actions/userCommandTypeActions';
import * as commandTypeApi from '../../api/commandTypeApi';
import { Loadable } from '../../models';
import { UserCommandTypes } from '../../models/state/userCommandTypesState';
import { selectUserCommandTypes } from '../../selectors/userCommandTypes';

const log = getLogger('simManagement.sagas.login');

/**
 * Ensure that the available commands types have been loaded.
 * @param action The action that invoked the saga.
 */
export function* ensureAllAvailableCommandTypesForUserSaga(action: Action<BasicSearchFilter>) {
  const filter = action.payload;
  if (typeof filter === 'undefined') {
    log.error('payload is undefined');
    return;
  }

  const availableCommands: Loadable<UserCommandTypes> = yield select(selectUserCommandTypes);
  if (
    !availableCommands.isLoading &&
    (availableCommands.companyId !== filter.companyId ||
      availableCommands.accountId !== filter.accountId ||
      (availableCommands.expiresOn && availableCommands.expiresOn < new Date()))
  ) {
    yield put(loadUserCommandTypes(filter));
  }
}

/**
 * Load the available command types for the current user, company & account.
 * @param action The action that invoked the saga.
 */
export function* loadAllAvailableCommandTypesForUserSaga(action: Action<BasicSearchFilter>) {
  const filter = action.payload;
  if (typeof filter === 'undefined') {
    log.error('payload is undefined');
    return;
  }

  yield put(loadUserCommandTypesPending(filter));
  try {
    const commandTypeIds: string[] = yield call(commandTypeApi.getAllAvailableCommandTypesForUser, filter);
    yield put(loadUserCommandTypesSuccess(filter, commandTypeIds));
  } catch (error: any) {
    yield put(loadUserCommandTypesFailure(filter, error));
  }
}

/*
 * Initialize all sagas related to companies
 */
export function* initUserCommandTypes() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_ALL_AVAILABLE_COMMAND_TYPES_FOR_USER, ensureAllAvailableCommandTypesForUserSaga);
  yield takeEvery(LOAD_ALL_AVAILABLE_COMMAND_TYPES_FOR_USER, loadAllAvailableCommandTypesForUserSaga);
}
